import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Pill, Input, Select, Textarea, EmptyState } from "../components/UI";
import { Plus, Target, Trash2 } from "lucide-react";

const TYPES = [
  { value: "first_party", label: "First-party" },
  { value: "third_party", label: "Third-party" },
  { value: "geo_lookback", label: "Geo lookback" },
  { value: "competitor_visitor", label: "Competitor visitors" },
  { value: "crm", label: "CRM" },
];
const LOOKBACKS = ["1h", "6h", "12h", "24h", "3d", "7d", "14d", "30d"];

export default function Audiences() {
  const [audiences, setAudiences] = useState([]);
  const [segments, setSegments] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("my");
  const [form, setForm] = useState({
    name: "", type: "first_party", description: "", estimated_reach: 0,
    cpm_data_fee: 0, provider: "", category: "", lookback_window: "",
    consent_confirmed: false, tags: "",
  });

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/audiences"), api.get("/catalog/segments")]);
    setAudiences(a.data); setSegments(b.data);
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    const payload = {
      ...form,
      estimated_reach: parseInt(form.estimated_reach) || 0,
      cpm_data_fee: parseFloat(form.cpm_data_fee) || 0,
      tags: form.tags ? form.tags.split(",").map(s => s.trim()).filter(Boolean) : [],
    };
    try {
      await api.post("/audiences", payload);
      setOpen(false); load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    }
  };

  const activateSegment = async (seg) => {
    await api.post("/audiences", {
      name: seg.name, type: "third_party", description: seg.name,
      estimated_reach: seg.estimated_reach, cpm_data_fee: seg.cpm_data_fee,
      provider: seg.provider, category: seg.category,
      consent_confirmed: true, polygon_ids: [], tags: ["3p"],
    });
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/audiences/${id}`); load();
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Audience Builder"
        title="Audiences"
        subtitle="First-party CRM, geo-lookback, competitor visitor, and 3rd-party segment activation."
        action={
          <Btn onClick={() => setOpen(true)} data-testid="new-audience-btn">
            <Plus className="w-4 h-4" /> Build audience
          </Btn>
        }
      />

      <div className="flex gap-2 border-b border-white/10">
        {[
          { id: "my", label: "My audiences" },
          { id: "catalog", label: "Data Marketplace" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`aud-tab-${t.id}`}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-white"
            }`}>{t.label}</button>
        ))}
      </div>

      {tab === "my" && (
        audiences.length === 0 ? (
          <Card><EmptyState icon={Target} title="No audiences" description="Create a first-party, geo-lookback, or 3P audience." /></Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {audiences.map(a => (
              <Card key={a.id} data-testid={`audience-${a.id}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="font-display text-lg font-bold text-white">{a.name}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{a.provider || "Internal"} · {a.category || "—"}</div>
                  </div>
                  <Pill status={a.consent_confirmed ? "Active" : "Pending"}>{a.type.replace("_", " ")}</Pill>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                  <div><div className="text-slate-500 uppercase tracking-wider">Reach</div><div className="font-mono text-white mt-0.5">{(a.estimated_reach || 0).toLocaleString()}</div></div>
                  <div><div className="text-slate-500 uppercase tracking-wider">Data fee</div><div className="font-mono text-white mt-0.5">${a.cpm_data_fee}/M</div></div>
                  <div><div className="text-slate-500 uppercase tracking-wider">Lookback</div><div className="font-mono text-white mt-0.5">{a.lookback_window || "—"}</div></div>
                </div>
                {a.description && <p className="mt-3 text-xs text-slate-400 line-clamp-2">{a.description}</p>}
                <div className="mt-3 flex justify-end">
                  <button onClick={() => handleDelete(a.id)} className="text-slate-500 hover:text-rose-300 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      {tab === "catalog" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="catalog-grid">
          {segments.map(s => (
            <Card key={s.id}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-cyan-300">{s.provider}</div>
                  <div className="font-display text-lg font-bold text-white mt-1">{s.name}</div>
                  <div className="text-xs text-slate-400 mt-1">{s.category}</div>
                </div>
                <Btn variant="secondary" onClick={() => activateSegment(s)} data-testid={`activate-${s.id}`}>Activate</Btn>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                <div><div className="text-slate-500 uppercase tracking-wider">Est. reach</div><div className="font-mono text-white mt-0.5">{s.estimated_reach.toLocaleString()}</div></div>
                <div><div className="text-slate-500 uppercase tracking-wider">Data fee</div><div className="font-mono text-white mt-0.5">${s.cpm_data_fee}/M CPM</div></div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-[#0a0e1a] border border-white/10 rounded-lg max-w-xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-bold mb-4">Build audience</h3>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="col-span-2" data-testid="audience-name-input" />
              <Select label="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
              <Select label="Lookback (geo only)" value={form.lookback_window} onChange={(e) => setForm({ ...form, lookback_window: e.target.value })}>
                <option value="">—</option>
                {LOOKBACKS.map(l => <option key={l}>{l}</option>)}
              </Select>
              <Input label="Estimated reach" type="number" value={form.estimated_reach} onChange={(e) => setForm({ ...form, estimated_reach: e.target.value })} />
              <Input label="CPM data fee" type="number" step="0.05" value={form.cpm_data_fee} onChange={(e) => setForm({ ...form, cpm_data_fee: e.target.value })} />
              <Input label="Provider" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} />
              <Input label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <div className="col-span-2"><Textarea label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm text-slate-300 col-span-2" data-testid="consent-label">
                <input type="checkbox" checked={form.consent_confirmed} onChange={(e) => setForm({ ...form, consent_confirmed: e.target.checked })} data-testid="consent-checkbox" />
                I confirm this data was collected lawfully and with required consent
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
              <Btn onClick={handleCreate} data-testid="save-audience-btn">Create audience</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
